import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import logo from "@/assets/logo.webp";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: "Privacy Policy — Business Dite Ads" },
      {
        name: "description",
        content:
          "How Business Dite Ads collects, uses, stores and protects your data — including account, company, GPS and payment information.",
      },
      { property: "og:title", content: "Privacy Policy — Business Dite Ads" },
      { property: "og:url", content: "https://busditeads.com/privacy" },
      {
        property: "og:description",
        content: "How Business Dite Ads collects, uses, stores and protects your data.",
      },
    ],
    links: [{ rel: "canonical", href: "https://busditeads.com/privacy" }],
  }),
});

function PrivacyPage() {
  const { t } = useTranslation();
  return (
    <LegalShell>
      <article className="prose prose-sm max-w-none dark:prose-invert sm:prose-base">
        <p className="text-xs text-muted-foreground">{t("legal.lastUpdated")}: 2026-04-22</p>
        <h1>{t("legal.privacy.title")}</h1>
        <p>{t("legal.privacy.intro")}</p>

        <h2>{t("legal.privacy.s1Title")}</h2>
        <p>{t("legal.privacy.s1Body")}</p>

        <h2>{t("legal.privacy.s2Title")}</h2>
        <p>{t("legal.privacy.s2Body")}</p>

        <h2>{t("legal.privacy.s3Title")}</h2>
        <p>{t("legal.privacy.s3Body")}</p>

        <h2>{t("legal.privacy.s4Title")}</h2>
        <p>{t("legal.privacy.s4Body")}</p>

        <h2>{t("legal.privacy.s5Title")}</h2>
        <p>{t("legal.privacy.s5Body")}</p>

        <h2>{t("legal.privacy.s6Title")}</h2>
        <p>{t("legal.privacy.s6Body")}</p>

        <h2>{t("legal.privacy.s7Title")}</h2>
        <p>
          {t("legal.privacy.s7Body")}{" "}
          <a href="mailto:contact@diteads.com" className="text-primary hover:underline">
            contact@diteads.com
          </a>
          .
        </p>
      </article>
    </LegalShell>
  );
}

export function LegalShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2">
            <img src={logo} alt="Business Dite Ads" className="h-9 w-9 object-contain" />
            <span className="text-base font-semibold tracking-tight">
              Business <span className="text-primary">Dite Ads</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            <Button
              asChild
              size="sm"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Link to="/auth">{mounted ? t("landing.signIn") : "Sign in"}</Link>
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-16">{children}</main>

      <footer className="border-t border-border/60 py-8">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 text-xs text-muted-foreground sm:flex-row sm:px-6">
          <div className="flex items-center gap-2">
            <img src={logo} alt="" className="h-5 w-5 object-contain" />
            <span>© {new Date().getFullYear()} Business Dite Ads</span>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-foreground">
              {t("landing.privacy")}
            </Link>
            <Link to="/terms" className="hover:text-foreground">
              {t("landing.terms")}
            </Link>
            <Link to="/refund" className="hover:text-foreground">
              {t("legal.refund.short")}
            </Link>
            <a href="mailto:contact@diteads.com" className="hover:text-foreground">
              {t("landing.contact")}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
